import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpDown, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SponsorLayout } from "@/components/sponsor/SponsorLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { SponsorChallengesSheet, SponsorShop } from "@/components/sponsor/SponsorChallengesSheet";

type Row = { application_id: string; full_name: string | null; business_name: string | null; business_type: string | null; community: string | null; parish: string | null; total_score: number | null };
type SortKey = "score" | "name" | "shop";

const location = (r: Row) => [r.community, r.parish].filter(Boolean).join(", ");
const score = (r: Row) => Number(r.total_score ?? 0);

export default function SponsorView() {
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");
  const [parish, setParish] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("score");
  const [asc, setAsc] = useState(false);
  const [openShop, setOpenShop] = useState<SponsorShop | null>(null);

  useEffect(() => {
    const prev = document.title;
    document.title = "Top Shop Participants | Entrepreneurs Across Borders";
    return () => { document.title = prev; };
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["sponsor", "list"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_top_shop_sponsor_list");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });
  const rows = data ?? [];

  // Rank is by score across all participants, independent of filters.
  const ranked = useMemo(() => {
    const sorted = [...rows].sort((a, b) => score(b) - score(a));
    return sorted.map((r, i) => ({ ...r, rank: i + 1 }));
  }, [rows]);

  const types = useMemo(() => [...new Set(rows.map((r) => r.business_type).filter(Boolean))] as string[], [rows]);
  const parishes = useMemo(() => [...new Set(rows.map((r) => r.parish).filter(Boolean))] as string[], [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = ranked.filter((r) => {
      if (q && ![r.full_name, r.business_name].some((v) => v?.toLowerCase().includes(q))) return false;
      if (type !== "all" && r.business_type !== type) return false;
      if (parish !== "all" && r.parish !== parish) return false;
      return true;
    });
    const d = asc ? 1 : -1;
    return list.sort((a, b) => {
      if (sortKey === "name") return d * (a.full_name ?? "").localeCompare(b.full_name ?? "");
      if (sortKey === "shop") return d * (a.business_name ?? "").localeCompare(b.business_name ?? "");
      return d * (score(a) - score(b));
    });
  }, [ranked, search, type, parish, sortKey, asc]);

  const stats = useMemo(() => ({
    count: rows.length,
    communities: new Set(rows.map((r) => r.community).filter(Boolean)).size,
    avg: rows.length ? rows.reduce((s, r) => s + score(r), 0) / rows.length : 0,
  }), [rows]);

  const toggle = (k: SortKey) => {
    if (sortKey === k) setAsc(!asc);
    else { setSortKey(k); setAsc(k !== "score"); }
  };

  const SortHead = ({ k, children }: { k: SortKey; children: string }) => (
    <TableHead>
      <button type="button" onClick={() => toggle(k)} className="inline-flex items-center gap-1 font-medium">{children}<ArrowUpDown className="h-3 w-3" /></button>
    </TableHead>
  );

  const notAuthorized = error && /not authorized/i.test((error as any).message ?? "");

  return (
    <SponsorLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Top Shop Challenge Participants</h1>
          <div className="mt-2 flex items-center gap-3">
            <span className="h-1 w-16 bg-[#F39001]" aria-hidden />
            <p className="text-lg text-muted-foreground">Crown the Best. Build the Rest.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : notAuthorized ? (
          <Card><CardContent className="p-6">Your sponsor access is not active. Please contact Entrepreneurs Across Borders at <a href="https://eaborders.org" className="underline">eaborders.org</a>.</CardContent></Card>
        ) : error ? (
          <Card><CardContent className="p-6 text-destructive">We could not load participants. Please refresh the page.</CardContent></Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[["Participants", stats.count], ["Communities represented", stats.communities], ["Average score", stats.avg.toFixed(1)]].map(([l, v]) => (
                <Card key={l as string}><CardContent className="p-4"><p className="text-sm text-muted-foreground">{l}</p><p className="text-2xl font-bold">{v}</p></CardContent></Card>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Input aria-label="Search" placeholder="Search name or shop" className="w-full sm:w-[260px]" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-[180px]" aria-label="Type of shop"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All types of shop</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={parish} onValueChange={setParish}>
                <SelectTrigger className="w-[160px]" aria-label="Parish"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All parishes</SelectItem>{parishes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {rows.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">Participants will appear here once they are accepted into the challenge.</CardContent></Card>
            ) : filtered.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No participants match your search.</CardContent></Card>
            ) : (
              <>
                <Card className="hidden md:block">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Rank</TableHead>
                          <SortHead k="name">Name</SortHead>
                          <SortHead k="shop">Shop Name</SortHead>
                          <TableHead>Type of Shop</TableHead>
                          <TableHead>Location</TableHead>
                          <SortHead k="score">Current Score</SortHead>
                          <TableHead><span className="sr-only">Challenges</span></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((r) => (
                          <TableRow key={r.application_id}>
                            <TableCell className="font-semibold">{r.rank}</TableCell>
                            <TableCell>{r.full_name ?? "-"}</TableCell>
                            <TableCell>{r.business_name ?? "-"}</TableCell>
                            <TableCell>{r.business_type ?? "-"}</TableCell>
                            <TableCell>{location(r) || "-"}</TableCell>
                            <TableCell className="font-semibold">{score(r).toFixed(1)}</TableCell>
                            <TableCell><Button size="sm" variant="outline" onClick={() => setOpenShop(r)}>View challenges</Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
                <div className="space-y-3 md:hidden">
                  {filtered.map((r) => (
                    <Card key={`m-${r.rank}-${r.business_name}`}>
                      <CardContent className="p-4 flex gap-4">
                        <div className="text-2xl font-bold text-primary w-8">{r.rank}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{r.business_name}</p>
                          <p className="text-sm">{r.full_name}</p>
                          <p className="text-xs text-muted-foreground">{[r.business_type, location(r)].filter(Boolean).join(" | ")}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2 text-right"><div><p className="text-xs text-muted-foreground">Score</p><p className="text-lg font-bold">{score(r).toFixed(1)}</p></div><Button size="sm" variant="outline" onClick={() => setOpenShop(r)}>View challenges</Button></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
      <SponsorChallengesSheet shop={openShop} onClose={() => setOpenShop(null)} />
    </SponsorLayout>
  );
}
